import { Autocomplete } from "@mui/material";
import { SortingState } from "@tanstack/react-table";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import CustomStyle from "Constants/Style";
import { app_modules } from "helpers/apps_modules_list";
import { ClipboardList } from "lucide-react";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { BoxSeam, ExclamationTriangle } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { setSelectedWarehouse } from "slices/Dropdowns/Warehouse/WarehouseDropdown/reducer";
import { fetchDamages } from "slices/Warehouse/ProjectWarehouse/Damage/thunk";
import { exportTransferHistoryCSV } from "slices/supervisor/stock/transferHistory/thunk";
import ProjectWarehouseTable from "./ProjectWarehouseTable/ProjectWarehouseTable";
const Damage: React.FC = () => {
  document.title = "Damage Products";
  const dispatch = useDispatch<any>();

  const selectTransferHistorys = createSelector(
    (state: any) => state.Damage,
    (pkg) => ({
      TransferHistorys: pkg.list,
      rowCount: pkg.rowCount,
      loading: pkg.loading,
      error: pkg.error,
    })
  );

  const { TransferHistorys, rowCount, loading, error } = useSelector(
    selectTransferHistorys
  );

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.data?.permissions || user?.permissions || [];
  const warehouse_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.warehouse?.app_name
  );

  const project_warehouse_permissions =
    warehouse_permissions?.modules_permissions?.find(
      (item: any) =>
        item?.modules_name ===
        app_modules?.warehouse?.modules?.stock_overview
    );

  const { warehouseDropdownList, warehouseDropdownLoading, selectedWarehouse } =
    useSelector(
      createSelector(
        (state: any) => state.warehouseDropdownReducer,
        (data) => ({
          warehouseDropdownList: data.list,
          warehouseDropdownLoading: data.loading,
          warehouseDropdownError: data.error,
          selectedWarehouse: data.selected,
        })
      )
    );

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [colValue, setColValue] = useState<any>();
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const navigate = useNavigate();

  const [editTransferHistory, setEditTransferHistory] =
    useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const location = useLocation();
  const currentPath = location.pathname;
  const statuses = [
    {
      label: "Total Sales Orders",
      icon: <BoxSeam size={18} />,           // 📄 Represents all SO documents
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
      subLabel: "All sales orders created",
    },
    {
      label: "Total Quantity",
      icon: <ClipboardList size={18} />,      // 📝 Active / processing orders
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 35,
      subLabel: "70 items inward today",
    },
    {
      label: "Robust Stock",
      icon: <BoxSeam size={18} />,              // 🚚 Delivered shipments
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
      subLabel: "Ready for dispatch",
    },
    {
      label: "Damaged Stock",
      icon: <ExclamationTriangle size={18} />,              // ⏳ Waiting for dispatch/delivery
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 82,
      subLabel: "3.2% of total stock",
    },
  ];
  useEffect(() => {
    if (!selectedWarehouse?.warehouse?.warehouse_id) return;
    dispatch(
      fetchDamages({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
        wharehouse_id: selectedWarehouse?.warehouse?.warehouse_id,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    selectedWarehouse?.warehouse?.warehouse_id,
    warehouseDropdownList,
  ]);

  useEffect(() => {
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters]);

  useEffect(() => {
    setData(TransferHistorys);
  }, [TransferHistorys]);
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // setSearch(searchInput);
      setPageIndex(0);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    // dispatch(fetchWarehouseDropdown());
  }, [dispatch]);



  const handleExport = async () => {
    try {
      const { blob, filename } = await exportTransferHistoryCSV();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      alert("Export failed");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Product Name",
        accessorKey: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/project-warehouse/damage_data/column_dropdown/?warehouse_id=${selectedWarehouse?.warehouse?.warehouse_id}&columns=item_name`,
      },
      {
        id: "damage",
        header: "Damage",
        accessorKey: "damage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/project-warehouse/damage_data/column_dropdown/?warehouse_id=${selectedWarehouse?.warehouse?.warehouse_id}&columns=damage`,
      },

      {
        header: "Unit",
        accessorKey: "unit",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/project-warehouse/damage_data/column_dropdown/?warehouse_id=${selectedWarehouse?.warehouse?.warehouse_id}&columns=unit`,
      },
      {
        header: "Make",
        accessorKey: "make_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/project-warehouse/damage_data/column_dropdown/?warehouse_id=${selectedWarehouse?.warehouse?.warehouse_id}&columns=make_name`,
      },
      {
        header: "Model",
        accessorKey: "model_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/project-warehouse/damage_data/column_dropdown/?warehouse_id=${selectedWarehouse?.warehouse?.warehouse_id}&columns=model_name`,
      },
      {
        header: "Category",
        accessorKey: "category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterFetchUrl: `/project-warehouse/damage_data/column_dropdown/?warehouse_id=${selectedWarehouse?.warehouse?.warehouse_id}&columns=category_name`,
      },

      {
        header: "Sub Category",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: "damage_quantity",
        header: "Damage Quantity",
        accessorKey: "damage",
        enableColumnFilter: false,
        enableSorting: false,
      },

      // {
      //   header: "Action",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => (
      //     <ul className="d-flex gap-2 list-unstyled mb-0">
      //       <li>
      //         <Link
      //           to={""}
      //           className="btn  btn-sm"
      //           style={{ backgroundColor: "#3F7DDA", color: "white" }}
      //         >
      //           View
      //         </Link>
      //       </li>
      //     </ul>
      //   ),
      // },

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;

          return (
            <ul className="d-flex gap-2 list-unstyled mb-0">
              <li>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: "#3F7DDA", color: "#FFF" }}
                  onClick={() =>
                    navigate(`/warehouse-damage-items-listings/${data?.id}`, {
                      state: data,
                    })
                  }
                >
                  View
                </button>
              </li>
            </ul>
          );
        },
      },
    ],
    [dispatch, TransferHistorys]
  );
  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-3">
            <AnalyticsCards statuses={statuses} />
          </Row>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Warehouse</h6>
                    </Col>
                  </Row>
                  <Row className="align-items-center justify-content-between mt-4 g-3 flex-wrap">
                    {/* Search Box */}
                    <Col xs={12} md={6} lg={4} xl={4}>
                      <div className="search-box w-100">
                        <Form.Control
                          type="text"
                          className="search"
                          placeholder="Search"
                          onChange={(e: any) => handleChange(e)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>

                    {/* Column Visibility + Actions */}
                    <Col xs={12} md={6} lg={8} xl={8} className="">
                      <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                        {/* Left side: Column Visibility */}
                        <div className="">
                          {tableInstance && (
                            <ColumnVisibilityDropdown
                              columns={tableInstance.getAllLeafColumns()}
                              excludedColumns={["action", "sr_no"]}
                            />
                          )}
                        </div>

                        {/* Right side: Actions */}
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <div style={{ minWidth: "200px" }}>
                            <Autocomplete
                              options={warehouseDropdownList}
                              loading={warehouseDropdownLoading}
                              getOptionLabel={(option: any) =>
                                option?.warehouse?.warehouse_name
                              }
                              value={selectedWarehouse}
                              onChange={(event, selected) => {
                                dispatch(setSelectedWarehouse(selected));
                              }}
                              disableClearable
                              sx={{ ...CustomStyle?.singleselect, minHeight: 38 }}
                              renderInput={(params) => (
                                <BootstrapTextField
                                  {...params}
                                  name="transfer_from"
                                  placeholder="Select Warehouse"
                                  fullWidth
                                  size="small"
                                />
                              )}
                            />
                          </div>

                          {project_warehouse_permissions?.permissions?.View ===
                            true && (
                              <div
                                className="hstack gap-2 btn btn-light"
                                onClick={handleExport}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                                Export
                              </div>
                            )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <div className="d-flex gap-3 mt-3">
                      <Link
                        to="/warehouse/stock-overview/daily-log"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath ===
                              "/warehouse/stock-overview/daily-log"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          Daily Log
                        </Button>
                      </Link>

                      <Link
                        to="/warehouse/stock-overview/in-stock"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath === "/warehouse/stock-overview/in-stock"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          In Stock
                        </Button>
                      </Link>

                      <Link
                        to="/warehouse/stock-overview/damage"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath === "/warehouse/stock-overview/damage"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          Damage
                        </Button>
                      </Link>
                    </div>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  {loading ? (
                    <div className="text-center py-4">
                      <h5>Loading...</h5>
                    </div>
                  ) : error ? (
                    <div className="">
                      {error?.message}
                      {/* <ProjectWarehouseTable
                        columns={columns}
                        // columns={[]}
                        data={data}
                        totalRecords={rowCount}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        onPageChange={setPageIndex}
                        onPageSizeChange={setPageSize}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        colValue={setFilterValue}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        isBordered={false}
                        isPagination={true}
                        isGlobalFilter={false}
                        SearchPlaceholder="Search TransferHistorys..."
                        manualSorting={true}
                        onTableReady={setTableInstance}
                      /> */}
                    </div>
                  ) : (
                    <div className="">
                      {/* <h5>Error: {error}</h5> */}
                      <ProjectWarehouseTable
                        columns={columns}
                        // columns={[]}
                        data={data}
                        totalRecords={rowCount}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        onPageChange={setPageIndex}
                        onPageSizeChange={setPageSize}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        colValue={setFilterValue}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        isBordered={false}
                        isPagination={true}
                        isGlobalFilter={false}
                        SearchPlaceholder="Search TransferHistorys..."
                        manualSorting={true}
                        onTableReady={setTableInstance}
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default Damage;
